import { memo, useState } from 'react';

import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { endOfDay, format, isToday, parseISO } from 'date-fns';
import {
  AlertCircle,
  ClipboardCheck,
  Compass,
  DoorOpen,
  Ellipsis,
  type LucideIcon,
  MessageSquare,
  Phone,
  StickyNote,
  UserCheck,
} from 'lucide-react';

import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { LeadUpdatedTimeAgo } from '@modules/_shared/components/lead/LeadUpdatedTimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatAddress,
  formatCompactNumber,
  formatPostcodeLocality,
} from '@modules/_shared/utils/formatting';
import {
  getHorizonLabel,
  translateAppraisalReason,
} from '@modules/_shared/utils/lead-labels';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import type {
  LeadKanbanItem,
  MenuAction,
} from '@modules/leads/LeadsKanbanView';
import { useLocale } from '@shared/hooks/locale';
import { abbrDateDifference } from '@shared/utils/abbr-date-difference';

type ItemMenuProps = {
  onUpdate: (action: MenuAction) => void;
};

const ItemMenu = ({ onUpdate }: ItemMenuProps) => {
  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    setAnchorEl(null);
  };

  return (
    <>
      <IconButton size="small" onClick={handleClick}>
        <Ellipsis size={18} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={e => {
            onUpdate({
              type: 'stage',
            });
            handleClose(e);
          }}
        >
          {t('Change stage')}
        </MenuItem>
      </Menu>
    </>
  );
};

const chipSx = {
  bgcolor: 'grey.100',
  height: 22,
  '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
};

const winChipSx = {
  height: 22,
  '& .MuiChip-label': { fontSize: '0.75rem' },
};

const activityIconMap: Record<string, LucideIcon> = {
  task: ClipboardCheck,
  call: Phone,
  visit: DoorOpen,
  assignment: UserCheck,
  note: StickyNote,
};

const LeadCard = memo(
  ({
    lead,
    onUpdate,
  }: {
    lead: LeadKanbanItem;
    onUpdate?: (action: MenuAction) => void;
  }) => {
    const { me } = useAppData();
    const { t, locale } = useLocale();
    const muiTheme = useTheme();
    const { text } = muiTheme;
    const palette = (muiTheme.vars ?? muiTheme).palette;
    const canViewLeadDetails = userCanViewLeadDetails(me, lead);

    const { contact, property } = lead;
    const contactName = !canViewLeadDetails
      ? lead.anonymized_contact
      : [contact?.first_name, contact?.last_name].filter(Boolean).join(' ');

    const contactLabel =
      contact == null && contactName == null
        ? t('No contact')
        : contactName === ''
        ? contact?.emails?.[0]?.email
        : contactName;

    const address = property
      ? formatAddress(property, ', ', false, {
          hideRoute: !canViewLeadDetails,
        })
      : lead.origin_enquiry_id != null
      ? formatPostcodeLocality(
          lead.appraisal_property_postcode,
          lead.appraisal_property_locality,
        )
      : '';

    const relationship = {
      owner: t('Owner'),
      past_owner: t('Past owner'),
      tenant: t('Tenant renter'),
      buyer: t('Buyer'),
      heir: t('Heir'),
      agent: t('Agent'),
      other: t('Other'),
      not_set: null,
    }[lead.relationship];

    const appraisal = property?.latest_appraisal;
    const appraisalMinMax = [
      appraisal?.min == null
        ? []
        : [formatCompactNumber(appraisal.min, locale)],
      appraisal?.max == null
        ? []
        : [formatCompactNumber(appraisal.max, locale)],
    ].join(' – ');

    const appraisalPerceived =
      lead.appraisal_perceived_value == null
        ? null
        : formatCompactNumber(lead.appraisal_perceived_value, locale);

    const nextActivity = lead.next_activity?.[0];
    const activityDate = nextActivity?.start_date
      ? endOfDay(parseISO(nextActivity.start_date))
      : nextActivity?.due_at
      ? parseISO(nextActivity.due_at)
      : null;
    const activity =
      nextActivity == null
        ? null
        : {
            type: nextActivity.activity_type,
            date: activityDate,
            overdue: nextActivity.due_at
              ? new Date(nextActivity.due_at) < new Date()
              : false,
          };

    const isTodayActivity =
      activity?.date != null ? isToday(activity.date) : false;
    const overdueBy =
      activity?.date != null
        ? abbrDateDifference(t, new Date(), activity.date, true).text
        : '';

    const ActivityIcon: LucideIcon =
      activity == null
        ? AlertCircle
        : activityIconMap[activity.type] ?? AlertCircle;

    const activityColor =
      activity == null
        ? palette.grey[600]
        : activity.overdue
        ? palette.error.main
        : isTodayActivity
        ? palette.warning.main
        : palette.success.main;

    const horizonLabel = getHorizonLabel(t, {
      appraisalReason: lead.appraisal_reason,
      saleHorizon: lead.sale_horizon,
      buyHorizon: lead.buy_horizon,
    });

    return (
      <Box
        sx={{
          py: 1,
        }}
      >
        {/* Header with name and menu */}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 0.75,
            pl: 2,
          }}
        >
          <Box sx={{ flex: 1, mr: 1, minWidth: 0 }}>
            <Typography
              sx={{
                ...text.ellipsis,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {contactLabel}
            </Typography>
            {address && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  ...text.ellipsis,
                  fontSize: '0.75rem',
                }}
              >
                {address}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              pr: onUpdate != null ? undefined : 2,
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            <LeadUpdatedTimeAgo
              shortVersion={true}
              lead={lead}
              showBothDate={me?.is_admin ?? false}
            />
            {onUpdate != null && <ItemMenu onUpdate={onUpdate} />}
          </Stack>
        </Stack>
        {/* Tags */}
        {(relationship ||
          lead.appraisal_reason !== 'not_set' ||
          horizonLabel) && (
          <Stack
            direction="row"
            sx={{
              mb: 1,
              pl: 2,
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {relationship && (
              <Chip label={relationship} size="small" sx={chipSx} />
            )}
            {lead.appraisal_reason !== 'not_set' && (
              <Chip
                label={translateAppraisalReason(t, lead.appraisal_reason)}
                size="small"
                sx={chipSx}
              />
            )}
            {horizonLabel && (
              <Chip label={horizonLabel} size="small" sx={chipSx} />
            )}
          </Stack>
        )}
        {(lead.win_probability || lead.predicted_win_date) && (
          <Stack
            direction="row-reverse"
            sx={{
              gap: 0.5,
              mb: 0.75,
              mr: 2,
            }}
          >
            {lead.win_probability && (
              <Chip
                label={lead.win_probability + '%'}
                color={lead.win_probability >= 50 ? 'success' : 'warning'}
                size="small"
                sx={winChipSx}
              />
            )}
            {lead.predicted_win_date && (
              <Chip
                label={format(
                  new Date(lead.predicted_win_date ?? ''),
                  'MMM yyyy',
                )}
                color="success"
                size="small"
                sx={winChipSx}
              />
            )}
          </Stack>
        )}
        {/* Values */}
        {(appraisalMinMax !== '' || appraisalPerceived != null) && (
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRadius: '8px',
              py: 0.75,
              px: 1.5,
              mx: 2,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {appraisalMinMax !== '' && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  <Compass size={15} color={palette.grey[600]} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {appraisalMinMax}
                  </Typography>
                </Stack>
              )}
              {appraisalPerceived != null && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  <MessageSquare size={15} color={palette.grey[600]} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {appraisalPerceived}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
        {/* Footer */}
        <Stack
          direction="row"
          sx={{
            px: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: activityColor,
            }}
          >
            <ActivityIcon size={16} color={activityColor} />
            {activity == null
              ? t('No activity')
              : activity.overdue
              ? t('overdueBy', {
                  time: overdueBy,
                })
              : t('dueIn', {
                  time: overdueBy,
                })}
          </Stack>
          {lead.broker && (
            <UserInfo user={lead.broker} anonymize={true} isSmall={true} />
          )}
        </Stack>
      </Box>
    );
  },
);

export default LeadCard;
